import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../Components/UI/RowLoader";
import ErrorAnimationComponent from "../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import axios from "axios";
import { getPartnersInPackage } from "../../apis";
import { toSnakeCase, transformKeys } from "../../_helper";
import { formatDate } from "../../_helper";
const PartnersListInPackage = () => {
  const { packageManagementId, packageId } = useParams();
  const [partnersIsPackageData, setPartnersIsPackageData] = useState([]);
  const [currentPackageName, setCurrentPackageName] = useState({});
  const [isError, setIsError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  useEffect(() => {
    setIsLoading(true);
    getPartnersInPackage(
      packageId,
      currentPage,
      searchTerm,
      sortKey,
      sortDirection,
      formatDate(startDate),
      formatDate(endDate)
    )
      .then((response) => {
        setCurrentPackageName({
          packageName: response.data.data.packageName,
          clientCategory: response.data.data.clientCategoryName,
        });
        const handleData = response.data.data.data.map((d) => transformKeys(d));
        setPartnersIsPackageData(handleData);
        setPagination({
          last_page: response.data.data.last_page,
          current_page: response.data.data.current_page,
        });
      })
      .catch((error) => {
        setIsError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    searchTerm,
    currentPage,
    sortKey,
    sortDirection,
    startDate,
    packageId,
    endDate,
  ]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [searchTerm, currentPage, sortKey, sortDirection, navigate]);

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    switch (column.sortKey) {
      case "Name":
        setSortKey("full_name");
        break;
      case "Email":
        setSortKey("email");
        break;
      case "JoinedAt":
        setSortKey("created_at");
        break;
      default:
        setSortKey(toSnakeCase(column.sortKey));
    }
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const filterAndSortData = () => {
    return partnersIsPackageData;
  };

  const hiddenColumns = ["id"]; // Add column keys you want to hide

  const customColumns = [
    {
      name: "Details",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`/profile/${row.id}`}>
            <EyeDetailsIcon width="30px" height="30px" />
          </Link>
        </div>
      ),
    },
  ];
  const columns = useMemo(() => {
    return createColumns(
      partnersIsPackageData,
      customColumns,
      hiddenColumns,
      true
    );
  }, [partnersIsPackageData]).map((col) => ({
    ...col,
    sortable: ["Name", "Email", "Joined At"].includes(col.name),

    width:
      col.name === "Num"
        ? "50px"
        : col.name === "Client Name"
        ? "200px"
        : "auto",

    cell:
      col.name === "Client Name"
        ? (row) => (
            <Link
              to={`/profile/${row.client_id}`}
              className="tw-underline hover:tw-text-primary"
            >
              {row.client_name}
            </Link>
          )
        : col.name === "Joined By"
        ? (row) => (
            <Link
              to={`/profile/${row.joined_by_id}`}
              className="tw-underline hover:tw-text-primary"
            >
              {row.joined_by}
            </Link>
          )
        : col.cell,
  }));

  if (isError) return <ErrorAnimationComponent message={isError} />;

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { data } = await axios.get(
        `/admin/package-management/partners/export/${packageId}`,
        {
          params: {
            search: searchTerm,
            sort_by: sortKey,
            sort_dir: sortDirection,
            date_from: formatDate(startDate),
            date_to: formatDate(endDate),
          },
          responseType: "blob",
        }
      );
      const fileName = `Partners_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <PageTitle
        BreadcrumbItems={[
          { title: "Home", link: "/" },
          { title: "Package Management", link: "/package-management" },
          {
            title: "Packages List",
            link: `/package-management/${packageManagementId}?filterBy=partner_type`,
          },
          {
            title: "Package Details",
            link: `/package-management/${packageManagementId}/package-details/${packageId}`,
          },
          { title: "Partners In Package" },
        ]}
      />
      <h2 className="h4 mt-3 tw-mb-5">
        Partners List In Package
        <span className="tw-text-primary tw-px-2">
          {currentPackageName?.packageName || ""}
        </span>
        For
        <span className="tw-text-primary tw-px-2">
          {currentPackageName?.clientCategory || ""}
        </span>
      </h2>
      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="tw-flex max-lg:tw-flex-col tw-gap-2 tw-justify-center tw-mb-2">
            <input
              type="text"
              onChange={handleFilter}
              value={searchTerm}
              className="border border-blue-300 rounded-lg p-2 max-lg:tw-w-full tw-w-1/2"
              placeholder="Search by name, mail , phone"
            />
            <div className="tw-flex tw-items-center tw-relative">
              <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                Date From:
              </span>
              <Flatpickr
                value={startDate}
                onChange={([date]) => setStartDate(date)}
                className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
              />
              {startDate && (
                <button
                  onClick={() => setStartDate(null)}
                  className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                >
                  x
                </button>
              )}
            </div>
            <div className="tw-flex tw-items-center tw-relative">
              <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28">
                Date For:
              </span>
              <Flatpickr
                value={endDate}
                onChange={([date]) => setEndDate(date)}
                className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
              />
              {endDate && (
                <button
                  onClick={() => setEndDate(null)}
                  className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                >
                  x
                </button>
              )}
            </div>
          </div>
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {partnersIsPackageData?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default PartnersListInPackage;
